import type { ComponentType } from "react";
import { useEffect, useState } from "react";
import type { DropdownTabsProps } from "app/pages/sales-intelligence/common-components/dropdown/DropdownTabs/DropdownTabs";

type ExpectedConsumerProps<T> = DropdownTabsProps<T>;

type ExpectedHOCProps = {
    defaultTabIndex?: number;
    isSearchEmpty: boolean;
};

const withDropdownSmartTabs = <T, PROPS extends ExpectedConsumerProps<T>>(ConsumerComponent: ComponentType<PROPS>) => {
    const WrappedWithDropdownSmartTabs = (props: PROPS & ExpectedHOCProps) => {
        const { isSearchEmpty, defaultTabIndex, tabs, ...rest } = props;
        const [selectedTabIndex, selectTabIndex] = useState(defaultTabIndex ?? 0);

        useEffect(() => {
            const isPossibleToMove = selectedTabIndex < tabs.length - 1;

            if (!isPossibleToMove || isSearchEmpty) {
                return;
            }

            const shouldMoveToNextTabWithResults =
                tabs[selectedTabIndex].items.length === 0 && tabs.some((tab) => tab.items.length > 0);

            if (!shouldMoveToNextTabWithResults) {
                return;
            }

            selectTabIndex(getNextTabIndexWithResults(selectedTabIndex, tabs));
        }, [tabs]);

        function getNextTabIndexWithResults(index: number, tabs: PROPS["tabs"]): number {
            if (tabs[index].items.length > 0) {
                return index;
            }

            return getNextTabIndexWithResults(index + 1, tabs);
        }

        return (
            <ConsumerComponent
                {...(rest as unknown as PROPS)}
                onTabSelect={selectTabIndex}
                selectedTabIndex={selectedTabIndex}
                tabs={tabs.map((tab) => ({ ...tab, isDisabled: tab.items.length === 0 }))}
            />
        );
    }

    return WrappedWithDropdownSmartTabs as ComponentType<
        ExpectedHOCProps & Omit<ExpectedConsumerProps<T>, "selectedTabIndex" | "onTabSelect">
    >;
};

export default withDropdownSmartTabs;
